import React from "react";
import { Box, Container, Typography, Stack, Button } from "@mui/material";
import { motion } from "framer-motion";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

const MONO   = "'JetBrains Mono', 'Fira Code', ui-monospace, monospace";
const ACCENT = "#E2684A";
const ACCENT2 = "#F3C969";
const PURPLE = "#7C3AED";
const BG     = "#030014";
const LINE   = "#1E2130";
const INK    = "#E7E9EE";
const INK2   = "#B6BBC6";
const INK3   = "#7D8390";

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 22 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.55, delay },
});

const SolutionHero = ({
  eyebrow,
  headline,
  highlightedText,
  subheadline,
  primaryCTA,
  primaryCTALink,
  secondaryCTA,
  secondaryCTALink,
}) => (
  <Box sx={{
    pt: { xs: 14, md: 17 }, pb: { xs: 9, md: 12 },
    borderBottom: `1px solid ${LINE}`,
    position: "relative", overflow: "hidden", background: BG,
  }}>
    {/* Static radial glows */}
    <Box sx={{
      position: "absolute", inset: 0, pointerEvents: "none",
      background: `
        radial-gradient(800px 400px at 70% -10%, rgba(124,58,237,0.10), transparent 60%),
        radial-gradient(500px 400px at 0% 90%, rgba(226,104,74,0.07), transparent 60%)
      `,
    }} />

    <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
      <Stack spacing={0} alignItems={{ xs: "flex-start", md: "center" }} textAlign={{ xs: "left", md: "center" }}>
        <motion.div {...fadeUp(0)}>
          <Typography sx={{
            fontFamily: MONO, fontSize: "12px", letterSpacing: "0.16em",
            textTransform: "uppercase", color: ACCENT, mb: 2,
          }}>
            {eyebrow || "Beanbag AI · Solutions"}
          </Typography>
        </motion.div>

        <motion.div {...fadeUp(0.08)}>
          <Typography sx={{
            fontFamily: "'IBM Plex Serif', serif",
            fontSize: { xs: "2.1rem", md: "3.2rem", lg: "3.8rem" },
            fontWeight: 600, letterSpacing: "-0.02em", lineHeight: 1.07,
            color: INK, mb: 3, maxWidth: 860,
          }}>
            {headline}{" "}
            <Box component="span" sx={{
              background: `linear-gradient(120deg, ${ACCENT}, ${ACCENT2})`,
              WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
            }}>
              {highlightedText}
            </Box>
          </Typography>
        </motion.div>

        <motion.div {...fadeUp(0.14)}>
          <Typography sx={{
            color: INK2, lineHeight: 1.75, maxWidth: 720,
            fontSize: { xs: "1.05rem", md: "1.15rem" }, mb: 4,
          }}>
            {subheadline}
          </Typography>
        </motion.div>

        <motion.div {...fadeUp(0.2)}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <Button
              variant="contained"
              size="large"
              endIcon={<ArrowForwardIcon />}
              onClick={() => window.location.href = primaryCTALink || "/contact-us"}
              sx={{ px: 4, py: 1.6, fontSize: "14px" }}
            >
              {primaryCTA || "Get Started"}
            </Button>
            {secondaryCTA && (
              <Button
                variant="outlined"
                size="large"
                onClick={() => window.location.href = secondaryCTALink || "#process"}
                sx={{ px: 4, py: 1.6, fontSize: "14px" }}
              >
                {secondaryCTA}
              </Button>
            )}
          </Stack>
        </motion.div>
      </Stack>
    </Container>
  </Box>
);

export default SolutionHero;
